import { REPORT_ERROR } from '~/pages/questionnaires/v2/customer/api/apiUrls';
import { clientApi } from './clientApi';
import { captureException } from "@sentry/react";
import { getClientIdFromStorage } from './getClientIdStorage';

interface ErrorReportPayload {
	id: string | number;
	chapter_name: string;
	other: Record<string, any>;
}

const REPORT_MAX_STRING_LENGTH = 1000;
const REPORT_MAX_ARRAY_ITEMS = 50;
const REPORT_MAX_OBJECT_KEYS = 50;
const REPORT_MAX_DEPTH = 6;

const truncateForReport = (value: unknown, depth = 0, seen = new WeakSet<object>()): unknown => {
	if (depth >= REPORT_MAX_DEPTH) return '[max depth exceeded]';

	if (typeof value === 'string') {
		return value.length > REPORT_MAX_STRING_LENGTH
			? `${value.slice(0, REPORT_MAX_STRING_LENGTH)}…[truncated, ${value.length} chars total]`
			: value;
	}

	if (value instanceof Error) {
		return {
			message: truncateForReport(value.message, depth + 1, seen),
			stack: truncateForReport(value.stack, depth + 1, seen),
		};
	}

	if (Array.isArray(value)) {
		const items = value
			.slice(0, REPORT_MAX_ARRAY_ITEMS)
			.map(item => truncateForReport(item, depth + 1, seen));
		if (value.length > REPORT_MAX_ARRAY_ITEMS) {
			items.push(`…[+${value.length - REPORT_MAX_ARRAY_ITEMS} more items]`);
		}
		return items;
	}

	if (value && typeof value === 'object') {
		if (seen.has(value)) return '[circular]';
		seen.add(value);

		const keys = Object.keys(value);
		const result: Record<string, unknown> = {};
		keys.slice(0, REPORT_MAX_OBJECT_KEYS).forEach(key => {
			result[key] = truncateForReport((value as Record<string, unknown>)[key], depth + 1, seen);
		});
		if (keys.length > REPORT_MAX_OBJECT_KEYS) {
			result.__truncated_keys__ = keys.length - REPORT_MAX_OBJECT_KEYS;
		}
		return result;
	}

	return value;
};

const sanitizeReportOther = (other: Record<string, any>): Record<string, any> =>
	truncateForReport(other) as Record<string, any>;

interface ResolvedClientId {
	id: string | number;
	source: 'param' | 'storage' | 'unknown';
	debug: {
		param_client_id: string | number | null;
		storage_client_id: string | null;
	};
}

const resolveClientIdForReport = (
	clientId: string | number | null | undefined
): ResolvedClientId => {
	const storageClientId = getClientIdFromStorage();
	const hasParam = clientId !== null && clientId !== undefined && clientId !== '';

	const id = hasParam ? clientId : storageClientId ?? 'unknown';
	const source: ResolvedClientId['source'] = hasParam ? 'param' : storageClientId ? 'storage' : 'unknown';

	return {
		id,
		source,
		debug: {
			param_client_id: hasParam ? clientId! : null,
			storage_client_id: storageClientId,
		},
	};
};

const sendErrorReportToBackend = async (payload: ErrorReportPayload): Promise<void> => {
	await clientApi.post(REPORT_ERROR, {
		data: {
			...payload,
			other: sanitizeReportOther(payload.other),
		},
	});
};

const buildReportErrorUrl = (): string => {
	const base = (import.meta.env.VITE_APP_API_URL || '').replace(/\/+$/, '');
	return `${base}${REPORT_ERROR}`;
};

const sendCrashReportReliably = async (payload: ErrorReportPayload): Promise<void> => {
	const body = JSON.stringify({
		data: {
			...payload,
			other: sanitizeReportOther(payload.other),
		},
	});
	const url = buildReportErrorUrl();

	await fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body,
		keepalive: true,
		credentials: 'omit',
	});
};

const recentlyReportedCrashSignatures = new Set<string>();
const CRASH_DEDUPE_WINDOW_MS = 5000;

const getCrashSignature = (error: unknown): string => {
	if (error instanceof Error) {
		const firstStackLine = (error.stack || '').split('\n')[1]?.trim() || '';
		return `${error.name}:${error.message}:${firstStackLine}`;
	}
	try {
		return String(error).slice(0, 200);
	} catch {
		return 'unknown_error';
	}
};

const shouldReportCrash = (signature: string): boolean => {
	if (recentlyReportedCrashSignatures.has(signature)) return false;
	recentlyReportedCrashSignatures.add(signature);
	setTimeout(() => recentlyReportedCrashSignatures.delete(signature), CRASH_DEDUPE_WINDOW_MS);
	return true;
};

const IGNORED_CRASH_MESSAGES = [
	/ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i,
	/^Script error\.?$/i,
	/^Non-Error promise rejection captured/i,
];

const EXTENSION_URL_RE = /^(chrome|moz|safari-web)-extension:\/\//i;

// Ошибки из скриптов браузерных расширений: все URL в стеке — extension://
const isExtensionCrash = (error: unknown): boolean => {
	const stackUrls = (error instanceof Error && error.stack?.match(/[a-z-]+:\/\/[^\s)]+/gi)) || [];
	return stackUrls.length > 0 && stackUrls.every(url => EXTENSION_URL_RE.test(url));
};

const isIgnoredCrash = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : String(error ?? '');
	return IGNORED_CRASH_MESSAGES.some(re => re.test(message)) || isExtensionCrash(error);
};

export const reportStepError = async (
	clientId: string | number,
	chapterName: string,
	values: Record<string, any>,
	error?: Record<string, any>
): Promise<void> => {
	const hasError = error && Object.keys(error)?.length > 0;
	const { id: resolvedClientId, source: clientIdSource, debug: clientIdDebug } =
		resolveClientIdForReport(clientId);

	const reportData: ErrorReportPayload = {
		id: resolvedClientId,
		chapter_name: chapterName,
		other: {
			user_agent: navigator?.userAgent || 'unknown',
			fields: values,
			client_id_debug: { source: clientIdSource, ...clientIdDebug },
			...(hasError && { traceError: error }),
		},
	};

	const sentryExtra = {
		id: resolvedClientId,
		chapter_name: chapterName,
		other: {
			user_agent: navigator?.userAgent || 'unknown',
			client_id_debug: { source: clientIdSource, ...clientIdDebug },
			...(hasError && { traceError: error }),
		}
	};

	try {
		await sendErrorReportToBackend(reportData);

		captureException(new Error(`Ошибка в шаге ${chapterName}`), {
			extra: sentryExtra,
		});

	} catch (err: unknown) {
		console.error('reportStepError: failed to send error report', err);

		captureException(err, {
			extra: sentryExtra,
			tags: {
				error_type: 'error_report_failed',
				chapter_name: chapterName,
			},
		});
	}
};

export const reportError = async (
	clientId: string | number,
	page: number,
	configSteps: string[],
	values: Record<string, any>,
	error?: Record<string, any>
): Promise<void> => {
	const chapterName = configSteps?.[page] || ``;
	await reportStepError(clientId, chapterName, values, error);
};

export const reportAppError = (
	clientId: string | number | null,
	source: string,
	error: unknown,
	extra?: Record<string, any>
): void => {
	const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');

	reportStepError(clientId ?? 'unknown', `app_error:${source}`, extra || {}, {
		reason: 'caught_exception',
		message: err.message,
		stack: err.stack,
		response: (error as any)?.response?.data,
	});
};

export type AppCrashSource = 'error_boundary' | 'window_error' | 'unhandled_rejection';

export const reportAppCrash = async (
	clientId: string | number | null,
	error: Error,
	componentStack?: string,
	eventId?: string,
	source: AppCrashSource = 'error_boundary'
): Promise<void> => {
	if (isIgnoredCrash(error)) return;
	if (!shouldReportCrash(getCrashSignature(error))) return;

	const { id: resolvedClientId, source: clientIdSource, debug: clientIdDebug } =
		resolveClientIdForReport(clientId);

	const reportData: ErrorReportPayload = {
		id: resolvedClientId,
		chapter_name: 'app_crash',
		other: {
			user_agent: navigator?.userAgent || 'unknown',
			url: window?.location?.href,
			client_id_debug: { source: clientIdSource, ...clientIdDebug },
			traceError: {
				source,
				message: error?.message,
				stack: error?.stack,
				componentStack,
				sentry_event_id: eventId,
			},
		},
	};

	try {
		await sendCrashReportReliably(reportData);
	} catch (err: unknown) {
		console.error('reportAppCrash: failed to send crash report', err);
	}
};
